import { createSlice } from "@reduxjs/toolkit";
import {
  getConfirmedWaterLevelCm,
  liveTelemetryLocationIds,
  resolveTelemetryDisplay,
} from "../../Utils/lidarTelemetryFields";
import { normalizeMqttId } from "../../Utils/telemetryConnectivityUtils";
import {
  getRainfallCumulativeMm,
  getRainfallIntensityMmPerMin,
  RAIN_ACTIVE_INTENSITY_MM_PER_MIN,
  resolveSensorTelemetryDisplay,
} from "../../Utils/sensorTelemetryFields";

const INITIAL_STATE = {
  liveByLocationId: {},
  liveByMqttId: {},
};

const MAX_RECENT_READINGS = 60;

function appendRecentReading(history = [], entry) {
  if (!entry?.t || entry.v == null) return history;
  const key = `${entry.t}|${entry.v}`;
  const next = [
    entry,
    ...history.filter((point) => `${point.t}|${point.v}` !== key),
  ];
  return next.slice(0, MAX_RECENT_READINGS);
}

function isNewerReading(incomingAt, existingAt) {
  if (!incomingAt) return true;
  if (!existingAt) return true;
  return new Date(incomingAt).getTime() >= new Date(existingAt).getTime();
}

function maxTelemetryTimestamp(...values) {
  let maxMs = 0;
  let maxValue = null;
  for (const value of values) {
    if (!value) continue;
    const ms = new Date(value).getTime();
    if (Number.isFinite(ms) && ms >= maxMs) {
      maxMs = ms;
      maxValue = value;
    }
  }
  return maxValue;
}

function writeLidarLive(bucket, key, { wl, blinkerActive, receivedAt }) {
  if (!key) return;
  const existing = bucket[key] ?? {};
  if (!isNewerReading(receivedAt, existing.wlReceivedAt)) return;

  bucket[key] = {
    ...existing,
    ...(wl != null
      ? {
          WL: wl,
          wlLastUpdated: receivedAt,
          recentWl: appendRecentReading(existing.recentWl, {
            t: receivedAt,
            v: wl,
          }),
        }
      : {}),
    ...(blinkerActive != null ? { blinkerActive } : {}),
    wlReceivedAt: receivedAt,
    telemetryLastUpdated: maxTelemetryTimestamp(
      receivedAt,
      existing.rainLastUpdated,
      existing.telemetryLastUpdated,
    ),
  };
}

function writeSensorLive(bucket, key, { rainInMM, rainIntensity, receivedAt }) {
  if (!key) return;
  const existing = bucket[key] ?? {};
  if (!isNewerReading(receivedAt, existing.rainReceivedAt)) return;

  const next = {
    ...existing,
    rainLastUpdated: receivedAt,
    rainReceivedAt: receivedAt,
  };

  if (rainInMM != null) {
    next.rainInMM = rainInMM;
    next.recentRainCumulative = appendRecentReading(
      existing.recentRainCumulative,
      { t: receivedAt, v: rainInMM },
    );
  }
  if (rainIntensity != null) {
    next.rainIntensityMmPerMin = rainIntensity;
    next.isRaining = rainIntensity > RAIN_ACTIVE_INTENSITY_MM_PER_MIN;
    next.recentRainIntensity = appendRecentReading(
      existing.recentRainIntensity,
      { t: receivedAt, v: rainIntensity },
    );
  } else {
    next.rainIntensityMmPerMin = null;
    next.isRaining = false;
  }

  next.telemetryLastUpdated = maxTelemetryTimestamp(
    receivedAt,
    existing.wlLastUpdated,
    existing.telemetryLastUpdated,
  );

  bucket[key] = next;
}

const telemetryLiveSlice = createSlice({
  name: "telemetryLive",
  initialState: INITIAL_STATE,
  reducers: {
    updateLidarTelemetryLive(state, action) {
      const payload = action.payload;
      const display = resolveTelemetryDisplay(payload);
      const wl = getConfirmedWaterLevelCm(display);
      const blinkerActive =
        display.blinkerActive === true
          ? true
          : display.blinkerActive === false
            ? false
            : null;
      if (wl == null && blinkerActive == null) return;

      const receivedAt = payload.receivedAt ?? payload.timestamp ?? null;
      const patch = { wl, blinkerActive, receivedAt };
      const mqttId = normalizeMqttId(payload.deviceId || display.deviceId);
      if (mqttId) {
        writeLidarLive(state.liveByMqttId, mqttId, patch);
      }
      for (const locationId of liveTelemetryLocationIds(payload)) {
        writeLidarLive(state.liveByLocationId, String(locationId), patch);
      }
    },
    updateSensorTelemetryLive(state, action) {
      const payload = action.payload;
      const display = resolveSensorTelemetryDisplay(payload);
      const rainInMM = getRainfallCumulativeMm(display);
      const rainIntensity = getRainfallIntensityMmPerMin(display);
      const wlRaw =
        display.confirmedLevelAvgCm ??
        display.lidarLevelCm ??
        display.waterLevelAvgCm;
      const wl =
        wlRaw === 0 || wlRaw === "0"
          ? 0
          : wlRaw == null || wlRaw === ""
            ? null
            : Number.isFinite(Number(wlRaw))
              ? Number(wlRaw)
              : null;
      const blinkerActive =
        display.blinkerActive === true
          ? true
          : display.blinkerActive === false
            ? false
            : null;
      const receivedAt = payload.receivedAt ?? payload.timestamp ?? null;

      if (
        rainInMM == null &&
        rainIntensity == null &&
        wl == null &&
        blinkerActive == null
      ) {
        return;
      }

      const mqttId = normalizeMqttId(payload.deviceId || display.deviceId);
      if (wl != null || blinkerActive != null) {
        const lidarPatch = { wl, blinkerActive, receivedAt };
        if (mqttId) writeLidarLive(state.liveByMqttId, mqttId, lidarPatch);
        for (const locationId of liveTelemetryLocationIds(payload)) {
          writeLidarLive(
            state.liveByLocationId,
            String(locationId),
            lidarPatch,
          );
        }
      }
      if (rainInMM == null && rainIntensity == null) return;

      const patch = { rainInMM, rainIntensity, receivedAt };
      if (mqttId) {
        writeSensorLive(state.liveByMqttId, mqttId, patch);
      }
      for (const locationId of liveTelemetryLocationIds(payload)) {
        writeSensorLive(state.liveByLocationId, String(locationId), patch);
      }
    },
    clearTelemetryLive(state) {
      state.liveByLocationId = {};
      state.liveByMqttId = {};
    },
    hydrateTelemetryLiveFromSites(state, action) {
      const sites = Array.isArray(action.payload) ? action.payload : [];
      for (const site of sites) {
        const locationId = String(
          site?.location_id ?? site?.locationId ?? site?.id ?? "",
        ).trim();
        const mqttId = normalizeMqttId(
          site?.device_mac ??
            site?.masterDeviceId ??
            site?.mqttId ??
            site?.deviceId,
        );
        const rawWl =
          site?.water_level ??
          site?.waterLevel ??
          site?.waterLevelCm ??
          site?.WL;
        const wl =
          rawWl === 0 || rawWl === "0"
            ? 0
            : rawWl == null || rawWl === ""
              ? null
              : Number.isFinite(Number(rawWl))
                ? Number(rawWl)
                : null;
        const blinkerActive =
          site?.blinker_active === true || site?.blinkerActive === true
            ? true
            : site?.blinker_active === false || site?.blinkerActive === false
              ? false
              : null;
        const receivedAt =
          site?.wl_last_updated ??
          site?.wlLastUpdated ??
          site?.last_update ??
          site?.lastUpdate ??
          site?.telemetryLastUpdated ??
          null;
        if (wl == null && blinkerActive == null) continue;
        const patch = { wl, blinkerActive, receivedAt };
        if (mqttId) writeLidarLive(state.liveByMqttId, mqttId, patch);
        if (locationId) {
          writeLidarLive(state.liveByLocationId, locationId, patch);
        }
      }
    },
  },
});

export const {
  updateLidarTelemetryLive,
  updateSensorTelemetryLive,
  clearTelemetryLive,
  hydrateTelemetryLiveFromSites,
} = telemetryLiveSlice.actions;
export default telemetryLiveSlice.reducer;
